import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useProducts } from '../context/ProductsContext';
import { useCalculator } from '../context/CalculatorContext';
import { useScenario } from '../context/ScenarioContext';
import { useTeam } from '../context/TeamContext';
import GlobalAssumptions from '../components/GlobalAssumptions/GlobalAssumptions';
import ProductForm from '../components/Products/ProductForm';
import ProductList from '../components/Products/ProductList';
import ResultsSummary from '../components/Results/ResultsSummary';
import Benchmarks from '../components/Analytics/Benchmarks';
import ExecutiveReport from '../components/Reports/ExecutiveReport';
import ScoringGuide from '../components/Help/ScoringGuide';
import TeamSetup from '../components/Teams/TeamSetup';
import { getAllBundles } from '../utils/hospitalBundles';
import { generateExecutiveReportPDF } from '../utils/pdfExport';
import Button from '../components/Common/Button';
import Card from '../components/Common/Card';

const Dashboard = () => {
  const { currentUser } = useAuth();
  const { products, addProduct, deleteAllProducts } = useProducts();
  const { results, inputs } = useCalculator();
  const { scenarios } = useScenario();
  const { teams, currentTeam } = useTeam();
  const [showBundles, setShowBundles] = useState(false);
  const [showBenchmarks, setShowBenchmarks] = useState(false);
  const [showExecutiveReport, setShowExecutiveReport] = useState(false);
  const [showScoringGuide, setShowScoringGuide] = useState(false);
  const [showTeamSetup, setShowTeamSetup] = useState(false);
  
  const isHospital = inputs.facilityType === 'hospital';
  const hospitalBundles = getAllBundles();

  const handleAddBundle = async (bundle) => {
    for (const product of bundle.products) {
      await addProduct(product);
    }
    setShowBundles(false);
  };

  const handleExportPDF = () => {
    if (results && results.totalFTEs > 0) {
      generateExecutiveReportPDF(results, inputs, products, currentUser?.email || 'Unknown User');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900">EHS Risk-Based Staffing Modeler Pro</h1>
          <p className="text-gray-600 mt-3">
            {isHospital ? '🏥 Academic Hospital Mode' : '🏭 General Industry Mode'} | Welcome, {currentUser?.email}
            {currentTeam && <span className="ml-2">| Team: {currentTeam.name}</span>}
          </p>
          
          {!currentTeam && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-blue-800 text-sm">
                <strong>Setup Required:</strong> Please set up your team to get started.
                <Button size="sm" onClick={() => setShowTeamSetup(true)} className="ml-2">
                  Setup Team
                </Button>
              </p>
            </div>
          )}
        </div>

        {/* Quick Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-3 rounded-lg shadow-lg">
            <div className="text-2xl font-bold">{products.length}</div>
            <div className="text-xs mt-1 opacity-90">Activities</div>
          </div>
          <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-3 rounded-lg shadow-lg">
            <div className="text-2xl font-bold">
              {results ? results.totalFTEs.toFixed(1) : '0.0'}
            </div>
            <div className="text-xs mt-1 opacity-90">FTE Required</div>
          </div>
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white p-3 rounded-lg shadow-lg">
            <div className="text-2xl font-bold">
              {results ? `$${Math.round(results.totalCost / 1000)}k` : '$0'}
            </div>
            <div className="text-xs mt-1 opacity-90">Annual Cost</div>
          </div>
          {isHospital && results && results.fireSafetyHours > 0 && (
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white p-3 rounded-lg shadow-lg">
              <div className="text-2xl font-bold">
                {Math.round(results.fireSafetyHours)}
              </div>
              <div className="text-xs mt-1 opacity-90">Fire Safety Hrs</div>
            </div>
          )}
          {isHospital && results && results.benchmarkFTE > 0 && (
            <div className={`p-3 rounded-lg shadow-lg text-white ${
              results.benchmarkStatus === 'good' ? 'bg-gradient-to-br from-green-500 to-green-600' :
              results.benchmarkStatus === 'warning' ? 'bg-gradient-to-br from-yellow-500 to-yellow-600' :
              'bg-gradient-to-br from-red-500 to-red-600'
            }`}>
              <div className="text-2xl font-bold">
                {results.benchmarkStatus === 'good' ? '✓' : '⚠'}
              </div>
              <div className="text-xs mt-1 opacity-90">Benchmark Status</div>
            </div>
          )}
        </div>

        {/* Main 3-Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Column 1: Global Assumptions */}
          <div className="lg:col-span-4 space-y-8">
            <Card title="Step 1: Configure Facility">
              <div className="bg-blue-50 p-4 rounded-lg mb-5">
                <p className="text-sm text-gray-700 leading-relaxed">
                  <strong>🎯 Start Here:</strong> Select your facility type and enter organizational details.{isHospital && ' Hospital mode enables fire safety and environmental calculations.'}
                </p>
              </div>
              <GlobalAssumptions />
            </Card>
          </div>

          {/* Column 2: Activities */}
          <div className="lg:col-span-4 space-y-8">
            {/* Quick Actions */}
            <Card title="Step 2: Add Activities">
              <div className="bg-green-50 p-4 rounded-lg mb-5">
                <p className="text-sm text-gray-700 leading-relaxed">
                  <strong>📋 Add Activities:</strong> Build your workload model by adding activities or using pre-configured bundles.
                </p>
              </div>
              
              {/* Bundle Button */}
              {isHospital && (
                <div className="mb-4">
                  <Button 
                    onClick={() => setShowBundles(!showBundles)}
                    variant="secondary"
                    fullWidth
                  >
                    {showBundles ? '✕ Close' : '📦 Load Hospital Bundle'}
                  </Button>
                </div>
              )}

              {/* Bundles Modal */}
              {showBundles && isHospital && (
                <div className="mb-4 p-4 bg-purple-50 border-2 border-purple-200 rounded-lg max-h-96 overflow-y-auto">
                  <h3 className="font-bold text-lg mb-3">Hospital Bundles</h3>
                  <div className="space-y-3">
                    {hospitalBundles.map((bundle) => (
                      <div key={bundle.id} className="bg-white p-3 rounded border border-gray-200 hover:border-purple-400 transition">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900">{bundle.name}</h4>
                            <p className="text-xs text-gray-600 mt-1">{bundle.description}</p>
                            <p className="text-xs text-gray-600 mt-1">{bundle.productCount} activities</p>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => handleAddBundle(bundle)}
                          >
                            Add
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <ProductForm />
            </Card>

            {/* Activity List */}
            <Card title="Your Activities">
              {products.length > 0 ? (
                <>
                  <div className="flex justify-between items-center mb-3">
                    <p className="text-sm text-gray-600">{products.length} activities added</p>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        if (window.confirm('Clear all activities? This cannot be undone.')) {
                          await deleteAllProducts();
                        }
                      }}
                    >
                      Clear All
                    </Button>
                  </div>
                  <ProductList />
                </>
              ) : (
                <div className="text-center py-8 text-gray-600">
                  <p className="text-4xl mb-2">📋</p>
                  <p className="text-sm">No activities yet. Add your first activity above.</p>
                </div>
              )}
            </Card>
          </div>

          {/* Column 3: Results */}
          <div className="lg:col-span-4 space-y-8">
            <Card title="Step 3: Review Results">
              <div className="bg-purple-50 p-4 rounded-lg mb-5">
                <p className="text-sm text-gray-700 leading-relaxed">
                  <strong>📊 Results:</strong> Real-time FTE calculations update automatically as you add activities.
                </p>
              </div>
              
              {results && results.totalFTEs > 0 ? (
                <>
                  <ResultsSummary />
                  
                  {/* Executive Report Button */}
                  <div className="mt-6">
                    <Button
                      onClick={() => setShowExecutiveReport(true)}
                      variant="primary"
                      fullWidth
                    >
                      📊 View Executive Report
                    </Button>
                  </div>
                  
                  {/* Benchmarks Toggle */}
                  {isHospital && results.benchmarkFTE > 0 && (
                    <div className="mt-4">
                      <Button
                        onClick={() => setShowBenchmarks(!showBenchmarks)}
                        variant="outline"
                        fullWidth
                      >
                        {showBenchmarks ? '✕ Hide Benchmarks' : '🎯 View Industry Benchmarks'}
                      </Button>
                      
                      {showBenchmarks && (
                        <div className="mt-4">
                          <Benchmarks />
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12 text-gray-600">
                  <p className="text-6xl mb-4">📊</p>
                  <p className="text-lg font-medium mb-2">No Results Yet</p>
                  <p className="text-sm">Configure your facility and add activities to see FTE calculations.</p>
                </div>
              )}
            </Card>
          </div>
        </div>

        {/* Bottom Help Section */}
        <div className="mt-10">
          <Card title="💡 Quick Guide">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">1️⃣ Facility Setup</h3>
                <ul className="text-sm text-gray-700 space-y-2 leading-relaxed">
                  <li>• Select facility type (Hospital or General)</li>
                  <li>• Enter employee count and org details</li>
                  {isHospital && <li>• Add hospital-specific data (beds, sq ft, labs)</li>}
                  <li>• Set salary and benefits</li>
                </ul>
              </div>
              
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">2️⃣ Build Workload</h3>
                <ul className="text-sm text-gray-700 space-y-2 leading-relaxed">
                  <li>• Add activities manually with risk weights</li>
                  {isHospital && <li>• Or use hospital bundles for quick start</li>}
                  {isHospital && <li>• Select department categories for multipliers</li>}
                  <li>• Enable/disable activities as needed</li>
                </ul>
              </div>
              
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">3️⃣ Analyze & Export</h3>
                <ul className="text-sm text-gray-700 space-y-2 leading-relaxed">
                  <li>• View real-time FTE calculations</li>
                  {isHospital && <li>• Compare to industry benchmarks</li>}
                  <li>• Review cost breakdowns</li>
                  <li>• Save scenarios for comparison</li>
                </ul>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Scoring Guide Modal */}
      {showScoringGuide && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setShowScoringGuide(false)}>
          <div className="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-green-600 to-green-700 px-8 py-6 flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-white">Activity Scoring Guide</h2>
                <p className="text-green-100 text-sm mt-1">How to determine Complexity, Risk, and Environment percentages</p>
              </div>
              <button
                onClick={() => setShowScoringGuide(false)}
                className="text-white hover:text-gray-200 transition-colors text-2xl font-bold px-3"
              >
                ×
              </button>
            </div>
            
            {/* Modal Body */}
            <div className="overflow-y-auto max-h-[calc(90vh-100px)] p-6 bg-gray-50">
              <ScoringGuide />
            </div>
          </div>
        </div>
      )}

      {/* Executive Report Modal */}
      {showExecutiveReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setShowExecutiveReport(false)}>
          <div className="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-6 flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-white">Executive Report</h2>
                <p className="text-blue-100 text-sm mt-1">FTE Justification & Analysis</p>
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={handleExportPDF}
                  variant="secondary"
                  size="sm"
                >
                  📄 Export PDF
                </Button>
                <button
                  onClick={() => setShowExecutiveReport(false)}
                  className="text-white hover:text-gray-200 transition-colors text-2xl font-bold px-3"
                >
                  ×
                </button>
              </div>
            </div>
            
            {/* Modal Body */}
            <div className="overflow-y-auto max-h-[calc(90vh-100px)] p-6 bg-gray-50">
              <ExecutiveReport />
            </div>
          </div>
        </div>
      )}
      
      {/* Team Setup Modal */}
      {showTeamSetup && (
        <TeamSetup onClose={() => setShowTeamSetup(false)} />
      )}
    </div>
  );
};

export default Dashboard;
